import React, { Component } from 'react';
import './features.css';
import logo from './logo.png';
import { Button } from '@fluentui/react-components';
import {
  Navigation24Regular,
  ChevronDown24Regular,
  ScalesFilled,
  TasksAppFilled,
  PeopleFilled,
  MailFilled,
  SearchFilled,
  SearchInfoFilled,
  OrganizationFilled,
  DocumentFilled,
  ArrowSyncFilled,
  CheckmarkCircleFilled,
} from '@fluentui/react-icons';
import NavBar from './NavBar';

interface FeaturesProps {}

interface FeaturesState {
  isMenuOpen: boolean;
  isProductsMenuOpen: boolean;
  isSolutionsMenuOpen: boolean;
  activeModule: string | null;
}

class Features extends Component<FeaturesProps, FeaturesState> {
  constructor(props: FeaturesProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isProductsMenuOpen: false,
      isSolutionsMenuOpen: false,
      activeModule: null,
    };
  }

  toggleMenu = () => this.setState({ isMenuOpen: !this.state.isMenuOpen });
  toggleProductsMenu = () => this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  closeProductsMenu = () => this.setState({ isProductsMenuOpen: false });
  toggleSolutionsMenu = () => this.setState({ isSolutionsMenuOpen: !this.state.isSolutionsMenuOpen });
  closeSolutionsMenu = () => this.setState({ isSolutionsMenuOpen: false });

  setActiveModule = (key: string) => {
    this.setState(prev => ({
      activeModule: prev.activeModule === key ? null : key,
    }));
  };

  render() {
    const { activeModule } = this.state;

    const modules = [
      {
        key: 'docManagement',
        name: 'Document Management',
        description: 'AI extraction · Annotation · Signatures · Guest portal',
        subtitle: '25 Features',
        iconClass: 'doc-mgmt',
        icon: <DocumentFilled />,
        groups: [
          {
            title: 'AI & Intelligence',
            items: [
              'AI Data Extraction auto-maps invoice, contract, ID, etc (15 different types of documents) and form fields to SharePoint columns without manual entry',
              'AI Summarization RAG-based summaries exported as Word or HTML, saved directly to OneDrive',
              'AI Email Tagging auto-generates title, category, and colour tag whenever an email is saved to an entity',
              'AI find patterns identifies trends and anomalies across documents to provide actionable insights',
            ],
          },
          {
            title: 'Security & Sharing',
            items: [
              'Guest Portal with PIN share documents with external parties; no Microsoft 365 account required',
              'Automatic Download Watermarking diagonal stamp + traced footer on every PDF download; original stays intact',
              'Permissions Management view, grant, and revoke sharing links per file from a single panel',
              'Built-in Digital Signatures multi-signer with sequential or parallel order; signed PDF auto-saved to SharePoint',
              'Built-in PurView labeling for sensitive documents, with AI-based label recommendation and auto-application on upload',
             'Built-in PurView Record Management for retention labels, with automatic application based on content and entity rules',
            ],
          },
          {
            title: 'Document Operations',
            items: [
              'Annotation & Permanent Redaction highlights, sticky notes, freehand drawing, and burn-redaction boxes',
              'Word-Level Version Comparison side-by-side diff: additions in green, deletions in red, synchronized scrolling',
              'Check-Out / Check-In locks a document during editing with per-publication comments and full audit trail',
              'Multi-Library Support OneDrive, SharePoint sites, and Teams Channels in one unified interface',
              'Full SharePoint Metadata Support map any library column to the entity record with AI-based auto-mapping suggestions on upload',
              'Metadata Rules Binding set required fields, default values, and conditional visibility rules based on document type or entity category',
              'Document Linking link any file to multiple entities; view all linked entities from the file card',
              'Document expiration set  expiration dates for documents and create alerts to notify users before they expire',
              'Document Lifecycle Automation trigger workflows based on document events like upload, modification, or approaching expiration',
              'Document Commenting & Collaboration shared comments with others, resolved/unresolved status, and email notifications on activity'
              
            ],
          },
          {
            title: 'Governance & Discovery',
            items: [
              'Compliance & Retention Labels apply Microsoft 365 Retention Labels directly from the document panel',
              'Per-File Change Notifications subscribe to exactly the document changes you care about; no broadcast spam',
              'Document Reviews & Ratings numeric rating with tracked status: Not Required → Commented → Accepted → Rejected',
              'Trending Documents ranked by usage and contextual relevance; recursive folder browsing included',
            ],
          },
        ],
      },
      {
        key: 'workflow',
        name: 'Workflow Designer',
        description: 'No-code automation · 36 triggers · 15 node types',
        subtitle: '42 Action Tools',
        iconClass: 'workflow',
        icon: <ArrowSyncFilled />,
        groups: [
          {
            title: '36 Event Triggers',
            items: [
              'Schedule (cron) daily, weekly, first of the month, or any custom interval',
              'Email received filter by sender, subject keywords, or body content before firing',
              'SharePoint & OneDrive fires on file upload, creation, or modification in any library',
              'Microsoft Teams channel post, chat message, or @mention triggers instant workflows',
              'HR events leave submission, contract expiry, training deadline, work anniversary',
              'Legal events contract expiring, hearing approaching, compliance due, case milestone updated',
            ],
          },
          {
            title: '15 Canvas Node Types',
            items: [
              'Action executes any of the 42 built-in tools with a smart auto-form per tool',
              'Condition / Switch if/else branching and multi-way switch on any field value',
              'Foreach & Do-Until loop through lists and poll until a condition is met',
              'Parallel Branches multiple paths run simultaneously then converge',
              'Approval sends email with Approve/Reject buttons; workflow pauses until clicked',
              'Email-Options sends email with custom choice buttons; branches on the response received',
              'Delay pause workflow for minutes, hours, or days then resume automatically',
            ],
          },
          {
            title: '42 Built-in Action Tools',
            items: [
              'Email (6) search inbox, send rich HTML, fetch thread, archive with AI tag, mark read, move to folder',
              'SharePoint (4) search, save file, link document to entity, write metadata columns',
              'Document Intelligence (4) extract structured data from invoices, contracts, receipts, and IDs',
              'Planner (2) fetch pending tasks, create task with title, assignee, due date, and priority',
              'Conversions (3) Office → PDF, data → Excel workbook, JSON → HTML table',
              'AI Analysis (3) compare documents, detect similarities, find patterns over a time range',
            ],
          },
          {
            title: 'Testing & Monitoring',
            items: [
              'Real-time Validation catches orphan nodes, missing parameters, and broken variable references before save',
              'Dry Run simulates the entire workflow without sending emails or modifying any files',
              'Execution History per-step record of input, output, and duration; find root cause of failures in seconds',
              '16 Ready-to-use Templates onboarding, invoice processing, contract expiry, legal case broadcast, and more',
            ],
          },
        ],
      },
      {
        key: 'hr',
        name: 'HR Module',
        description: 'Employee lifecycle · Leave automation · Training compliance',
        subtitle: '12 Features',
        iconClass: 'hr',
        icon: <PeopleFilled />,
        groups: [
          {
            title: 'Employee Lifecycle',
            items: [
              'Employee 360° Profile entity-based record linking all files, emails, tasks, and conversation history',
              'Onboarding Automation tasks, welcome emails, and training assignments triggered automatically on hire date',
              'Offboarding Workflow asset return checklist, access revocation, and exit interview scheduling',
              'Anniversary & Milestone Alerts automated congratulations and benefit-trigger reminders with no manual tracking',
            ],
          },
          {
            title: 'Leave & Time Management',
            items: [
              'Leave Request & Approval employee submits in Teams; manager approves or rejects via email button, no portal needed',
              'Leave Balance Tracking real-time remaining days per employee per leave type, always up to date',
              'Calendar Integration approved leave syncs to team shared calendar automatically on manager decision',
              'SLA-based Escalation unanswered leave requests escalate to HR admin after a configurable threshold',
            ],
          },
          {
            title: 'Performance & Compliance',
            items: [
              'Performance Review Cycles structured review forms with history, rating trends, and manager sign-off',
              'Training Compliance Tracking deadlines, completion status, and automatic reminders before due dates',
              'Contract Expiry Alerts 30 / 60 / 90-day warning chain for fixed-term employees with manager cc',
              'HR Analytics Dashboard headcount, leave trends, review completion rates, and open position stats',
            ],
          },
        ],
      },
      {
        key: 'legal',
        name: 'Legal Module',
        description: 'Case management · Contracts · Compliance deadlines · Client portal',
        subtitle: '12 Features',
        iconClass: 'legal',
        icon: <ScalesFilled />,
        groups: [
          {
            title: 'Case Management',
            items: [
              'Legal Case Entity all case files, emails, Planner tasks, hearings, and parties in a single Teams workspace',
              'Hearing & Deadline Tracking calendar view with configurable pre-hearing reminders sent automatically',
              'Case Status Workflow Open → Active → Pending Ruling → Closed with full audit trail on every transition',
              'Legal Team Assignment assign attorneys, paralegals, and external counsel per case with role-based access',
            ],
          },
          {
            title: 'Contract Lifecycle',
            items: [
              'Contract Entity full version history, annotation support, and clause-level AI extraction',
              'Contract Expiry Automation 30-day email chain + Planner task + manager alert, all triggered automatically',
              'AI Clause Extraction auto-identify parties, effective dates, obligations, and risk clauses on upload',
              'Digital Signature Integration collect signatures from all parties; final signed PDF auto-saved to SharePoint',
            ],
          },
          {
            title: 'Compliance & Reporting',
            items: [
              'Compliance Deadline Tracker statutory deadlines with automatic escalation on missed dates',
              'Conflict of Interest Check run before case assignment against the full existing client registry',
              'External Counsel Guest Portal share case documents with PIN; counsel needs no Microsoft 365 account',
              'Legal Analytics case volume by category, average resolution time, and attorney workload reports',
            ],
          },
        ],
      },
      {
        key: 'ticketing',
        name: 'Ticketing Module',
        description: 'Multi-domain issue tracking · SLA · 5 views · AI triage',
        subtitle: '30+ Automations',
        iconClass: 'ticketing',
        icon: <TasksAppFilled />,
        groups: [
          {
            title: 'Issue Management',
            items: [
              '12 issue type categories IT Support, HR Request, Legal Query, Finance, Facilities, and more',
              '5 priority levels each with an independent SLA clock per category and priority combination',
              'Auto-assignment Rules route tickets to the right team or individual by type, keyword, or department',
              'Bulk Operations update status, priority, assignee, or labels across multiple tickets simultaneously',
            ],
          },
          {
            title: 'Views & Collaboration',
            items: [
              '5 view modes Kanban board, List, Calendar, Timeline (Gantt), and Analytics dashboard',
              'Teams Notifications automatic update per ticket event; no separate portal to monitor',
              'Customer Portal submit and track tickets without a Microsoft 365 account using a PIN-secured link',
              'Internal vs. Customer Comments keeps internal resolution notes private from the submitter',
            ],
          },
          {
            title: 'Automation & Intelligence',
            items: [
              '30+ workflow triggers ticket created, priority changed, SLA breached, status updated, assignee changed',
              'SLA Breach Alerts escalate to manager before the deadline is missed, not after it expires',
              'AI Triage auto-categorize and suggest the best assignee based on ticket description and history',
              'Knowledge Base Integration suggests relevant articles at ticket submission to enable self-service',
              'Analytics volume trends, resolution time, SLA compliance rate, and team workload heat map',
            ],
          },
        ],
      },
    ];

    const activeMod = modules.find(m => m.key === activeModule) ?? null;

    return (
      <div className="features-page">

        {/* ── Navigation ─────────────────────────── */}
        <NavBar />

        {/* ── Hero ───────────────────────────────── */}
        <section className="features-hero-section">
          <div className="features-hero-content">
            <div className="features-hero-text">
              <div className="features-hero-badge">Platform Overview · All Modules</div>
              <h1 className="features-hero-title">
                pEntities <br />
                <span>Every Module. Every Feature.</span>
              </h1>
              <p className="features-hero-subtitle">
                pEntities is a business entity management platform built natively inside Microsoft Teams.
                Every contract, legal case, HR file, or project becomes a live workspace that brings
                documents, emails, tasks, people, and AI together in one intelligent environment. Explore
                each module below to see exactly what it delivers.
              </p>
              <div className="features-hero-buttons">
                <Button appearance="primary" size="large" className="features-cta-button"
                  onClick={() => window.open('https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled', '_blank')}>
                  Schedule Demo
                </Button>
              </div>
            </div>
            <div className="features-hero-stats">
              <div className="features-stat">
                <div className="features-stat-number">6</div>
                <div className="features-stat-label">Core Modules</div>
              </div>
              <div className="features-stat">
                <div className="features-stat-number">42</div>
                <div className="features-stat-label">Workflow Tools</div>
              </div>
              <div className="features-stat">
                <div className="features-stat-number">36</div>
                <div className="features-stat-label">Event Triggers</div>
              </div>
              <div className="features-stat">
                <div className="features-stat-number">100%</div>
                <div className="features-stat-label">Microsoft 365 Native</div>
              </div>
            </div>
          </div>
        </section>

        {/* â”€â”€ Modules Section â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <section className="features-tree-section">
          <div className="features-tree-container">

            <div className="features-tree-section-header">
              <h2>Explore the Platform</h2>
              <p>Select a module to explore its full feature set</p>
            </div>

            {/* Root Node */}
            <div className="features-root-node">
              <div className="features-root-icon-wrapper">
                <OrganizationFilled />
              </div>
              <div className="features-root-content">
                <h2>pEntities Platform</h2>
                <p>
                  A Microsoft 365-native platform that turns every business entity  a contract, a legal case,
                  an HR record, a project  into a live, intelligent workspace. It embeds directly in Microsoft
                  Teams and uses SharePoint for storage, Exchange for emails, Planner for tasks, and Azure AI
                  for intelligence. No new portals. No new logins. No double data entry. Deployed as an Azure
                  Managed Application inside your own tenant â€” your data stays yours.
                </p>
                <div className="features-root-tags">
                  <span className="features-root-tag">Microsoft Teams Native</span>
                  <span className="features-root-tag">Azure Managed App</span>
                  <span className="features-root-tag">Tenant Isolated</span>
                  <span className="features-root-tag">No-Code Automation</span>
                  <span className="features-root-tag">AI-Powered</span>
                  <span className="features-root-tag">AppSource Ready</span>
                </div>
              </div>
            </div>

            {/* Module Cards Row */}
            <div className="features-modules-row">
              {modules.map(m => (
                <div
                  key={m.key}
                  className={`features-module-card features-module-card--${m.iconClass}${activeModule === m.key ? ' active' : ''}`}
                  onClick={() => this.setActiveModule(m.key)}
                >
                  <div className="features-module-card-icon">{m.icon}</div>
                  <h3 className="features-module-card-name">{m.name}</h3>
                  <p className="features-module-card-desc">{m.description}</p>
                  <span className="features-module-card-badge">{m.subtitle}</span>
                  <ChevronDown24Regular
                    className={`features-module-card-chevron${activeModule === m.key ? ' open' : ''}`}
                  />
                </div>
              ))}
            </div>

            {/* Expanded Panel */}
            {activeMod && (
              <div className={`features-module-panel features-module-panel--${activeMod.iconClass}`}>
                <div className="features-panel-header">
                  <div className="features-panel-icon">{activeMod.icon}</div>
                  <div className="features-panel-header-text">
                    <h3>{activeMod.name}</h3>
                    <p>{activeMod.description}</p>
                  </div>
                  <span className="features-panel-badge">{activeMod.subtitle}</span>
                </div>
                <div className="features-panel-groups">
                  {activeMod.groups.map((group, gi) => (
                    <div key={gi} className="features-panel-group">
                      <h4 className="features-panel-group-title">{group.title}</h4>
                      <ul className="features-panel-list">
                        {group.items.map((item, ii) => (
                          <li key={ii} className="features-panel-list-item">
                            <CheckmarkCircleFilled className="features-panel-bullet" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </section>
        {/* ── CTA Section ─────────────────────────── */}
        <section className="features-cta-section">
          <div className="features-cta-inner">
            <h2>See pEntities in Action</h2>
            <p>
              Every feature on this page is live, production-ready, and running inside Microsoft Teams
              today. Book a personalised demo and we will walk through exactly the modules your team needs.
            </p>
            <div className="features-cta-buttons">
              <Button appearance="outline" size="large" className="features-cta-secondary"
                onClick={() => window.open('https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled', '_blank')}>
                Book a Demo
              </Button>
            </div>
          </div>
        </section>

        {/* ── Footer ──────────────────────────────── */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Solutions</h4>
                <ul>
                  <li><a href="/solutions/case-management">Legal Case Management</a></li>
                  <li><a href="/solutions/hr-template">Transform HR Operations</a></li>
                  <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                  <li><a href="/solutions/automation">Automate with AI Agents</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans &amp; Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou &amp; Minotavrou Streets, 54627</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms &amp; Conditions</a>
              </div>
            </div>
          </div>
        </footer>

      </div>
    );
  }
}

export default Features;
